import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { BalanceCurrency, CURRENCY_DATA, CurrencyCode, getCurrencySign, NumberFormatConfig } from '@gs-2.0/core';

import { DropdownItem } from '../dropdown/dropdown.component';
import { calculateDropdownPosition } from '../../utils/dropdown-position.util';

/**
 * Currency management component that provides currency selection functionality.
 * Uses content-card as the container with optional badge in the title area.
 * Based on Figma design: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36054
 * Dropdown design: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=90-14542
 *
 * **IMPORTANT**: This is a controlled component. You must update the `selectedCurrency` input
 * when handling the `currencySelected` output event for the display to update.
 *
 * Features:
 * - Dropdown closes automatically when clicking outside
 * - Displays currency in format: "CODE – Full Name Symbol" (e.g., "USD – United States Dollar $")
 * - Optional balance display with currency indicator
 * - Dropdown shows balance for each currency via secondaryText
 *
 * **Data Format Requirements**:
 * - Currency labels should use en dash (–) not hyphen (-): "USD – United States Dollar $"
 * - secondaryText should contain balance in format: "Total : X,XXX.XX"
 * - The data object can store additional info like symbol, countryCode
 *
 * @example
 * ```typescript
 * // OPTION 1: Using BalanceCurrency objects from API (Recommended)
 * const balanceCurrencies: BalanceCurrency[] = [
 *   {
 *     id: 'USD',
 *     symbol: '$',
 *     balance: 4789.58,
 *     availableAmountFundCash: 4789.58,
 *     hasFundCash: true
 *   },
 *   {
 *     id: 'EUR',
 *     symbol: '€',
 *     balance: 150.58,
 *     availableAmountFundCash: 150.58,
 *     hasFundCash: true
 *   }
 * ];
 *
 * // Component automatically transforms to dropdown format with:
 * // - Label: "USD – United States Dollar $"
 * // - secondaryText: "Total : 4,789.58"
 *
 * // OPTION 2: Using pre-formatted DropdownItem[] (manual format)
 * const availableCurrencies: DropdownItem[] = [
 *   {
 *     id: 'USD',
 *     label: 'USD – United States Dollar $',
 *     secondaryText: 'Total : 4,789.58',
 *     data: { symbol: '$', countryCode: 'US' }
 *   },
 *   {
 *     id: 'EUR',
 *     label: 'EUR – Euro €',
 *     secondaryText: 'Total : 150.58',
 *     data: { symbol: '€', countryCode: 'EU' }
 *   },
 *   {
 *     id: 'GBP',
 *     label: 'GBP – British Pound Sterling £',
 *     secondaryText: 'Total : 1,000.58',
 *     data: { symbol: '£', countryCode: 'GB' }
 *   }
 * ];
 *
 * currentCurrency = signal<DropdownItem>(availableCurrencies[0]);
 *
 * onCurrencyChange(currency: DropdownItem) {
 *   this.currentCurrency.set(currency);
 *   // Additional logic...
 * }
 * ```
 *
 * ```html
 * <!-- Using BalanceCurrency from API -->
 * <app-currency-management
 *   [balanceCurrencies]="balanceCurrencies"
 *   [selectedCurrency]="currentCurrency()"
 *   (currencySelected)="onCurrencyChange($event)">
 * </app-currency-management>
 *
 * <!-- Using pre-formatted DropdownItem[] -->
 * <app-currency-management
 *   [currencies]="availableCurrencies"
 *   [selectedCurrency]="currentCurrency()"
 *   (currencySelected)="onCurrencyChange($event)">
 * </app-currency-management>
 *
 * <!-- With balance and badge -->
 * <app-currency-management
 *   [balanceCurrencies]="balanceCurrencies"
 *   [selectedCurrency]="currentCurrency()"
 *   [balance]="'$1,613.75'"
 *   [badgeLabel]="'Active'"
 *   [badgeVariant]="'success'"
 *   (currencySelected)="onCurrencyChange($event)">
 * </app-currency-management>
 *
 * <!-- Custom title and subtitle -->
 * <app-currency-management
 *   [balanceCurrencies]="balanceCurrencies"
 *   [selectedCurrency]="currentCurrency()"
 *   title="Currency Settings"
 *   subtitle="Choose your display currency"
 *   (currencySelected)="onCurrencyChange($event)">
 * </app-currency-management>
 * ```
 */
@Component({
  selector: 'app-currency-management',
  templateUrl: './currency-management.component.html',
  styleUrls: ['./currency-management.component.scss'],
  standalone: false,
})
export class CurrencyManagementComponent {
  private elementRef = inject(ElementRef);

  /**
   * Reference to the dropdown wrapper element for position calculations
   */
  private dropdownWrapper = viewChild<ElementRef>('dropdownWrapper');

  /**
   * Array of BalanceCurrency objects from the API.
   * These will be automatically transformed to DropdownItem format.
   * Use this input when working with API data.
   */
  balanceCurrencies = input<BalanceCurrency[]>([]);

  /**
   * Array of pre-formatted DropdownItem objects for the dropdown.
   * Use this input when you want full control over formatting.
   * Each item should have:
   * - id: Currency code (e.g., 'USD')
   * - label: Formatted display text (e.g., 'USD – United States Dollar $')
   * - secondaryText: Balance display (e.g., 'Total : 4,789.58')
   * - data: Optional object with symbol, countryCode, etc.
   */
  currencies = input<DropdownItem[]>([]);

  /**
   * Currently selected currency item
   */
  selectedCurrency = input<DropdownItem | null>(null);

  /**
   * Title displayed in the card header
   */
  title = input<string>('fund.currency_management.title');

  /**
   * Optional subtitle displayed below the title
   */
  subtitle = input<string | undefined>('fund.currency_management.subtitle');

  /**
   * Optional badge label to display on the right side of the title
   */
  badgeLabel = input<string | undefined>(undefined);

  /**
   * Visual variant of the badge
   */
  badgeVariant = input<'warning' | 'success' | 'error' | 'info'>('success');

  /**
   * Balance amount to display
   * Can be a formatted string like "$1,234.56" or "1234.56"
   */
  balance = input<string | undefined>(undefined);

  /**
   * Optional label for the balance display
   * e.g., "Available Balance", "Total", "Current Balance"
   */
  balanceLabel = input<string | undefined>(undefined);

  /**
   * Custom icon for the card (defaults to globe icon)
   */
  icon =
    input<string>(`<svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12.4999 23.8337C18.2529 23.8337 22.9166 18.9834 22.9166 13.0003C22.9166 7.01724 18.2529 2.16699 12.4999 2.16699C6.74695 2.16699 2.08325 7.01724 2.08325 13.0003C2.08325 18.9834 6.74695 23.8337 12.4999 23.8337Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12.4999 2.16699C9.82516 5.08783 8.33325 8.96679 8.33325 13.0003C8.33325 17.0339 9.82516 20.9128 12.4999 23.8337C15.1747 20.9128 16.6666 17.0339 16.6666 13.0003C16.6666 8.96679 15.1747 5.08783 12.4999 2.16699Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.08325 13H22.9166" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`);

  /**
   * Icon color for the card icon
   */
  iconColor = input<string>('white');

  /**
   * Background color for the icon container
   * Defaults to blue (#4F46E5) based on Figma design
   */
  iconBackgroundColor = input<string>('');

  /**
   * Border of the card
   * Defaults to red border based on Figma design
   */
  cardBorderColor = input<string>('2px solid rgba(150, 8, 11, 0.22)');

  /**
   * Background of the card
   * Defaults to subtle gradient based on Figma design
   */
  cardBackgroundColor = input<string>(
    'linear-gradient(90deg, #FFF8F8 0%, #FFF1F1 50%, #FFF8F8 100%)'
  );

  /**
   * Maximum height for the dropdown
   */
  maxDropdownHeight = input<string>('400px');

  /**
   * Whether balance currencies are currently loading
   * When true, hides the currency indicator
   */
  isLoading = input<boolean>(false);

  /**
   * Number format configuration from global config
   */
  numberFormat = input<NumberFormatConfig>({
    decimalSymbol: '.',
    digitGroupingSymbol: ',',
    digitGrouping: '123,456,789',
  });

  /**
   * Emits when a currency is selected from the dropdown
   */
  currencySelected = output<DropdownItem>();

  /**
   * Internal signal to track dropdown open/close state
   */
  protected isDropdownOpen = signal<boolean>(false);

  /**
   * Internal signal to track whether dropdown should open above or below
   */
  protected dropdownOpenAbove = signal<boolean>(false);

  /**
   * Computed signal that returns the currencies to display in the dropdown.
   * Prioritizes balanceCurrencies (transforming them) over currencies input.
   */
  protected displayCurrencies = computed<DropdownItem[]>(() => {
    const balanceCurr = this.balanceCurrencies();
    const manualCurr = this.currencies();

    // If balanceCurrencies provided, transform and use them
    if (balanceCurr.length > 0) {
      return this.transformBalanceCurrencies(balanceCurr);
    }

    // Otherwise use manually provided currencies
    return manualCurr;
  });

  /**
   * Computed signal that maps the balance's digit count to a font-size tier so very
   * large balances (trillions and beyond) shrink instead of overlapping the flag/code.
   * Digit count includes the 2 decimal places (matching the formatted `balance` string):
   * billions = 12 digits, trillions = 15, quadrillions = 18.
   *
   * - `''`      → base 24px (`--typography-fontSize-xl`), up to hundreds of billions
   * - `'long'`  → 20px (`--typography-fontSize-base`), trillions (15–17 digits)
   * - `'longer'`→ 18px (`--typography-fontSize-lg`), quadrillions+ (18+ digits)
   *
   * Applied via the `&__balance-amount--long` / `--longer` modifier classes.
   */
  protected balanceSizeTier = computed<'' | 'long' | 'longer'>(() => {
    // Strip out all non-digit characters (commas, decimal points, spaces, currency symbols)
    const digits = (this.balance() ?? '').replace(/\D/g, '').length;

    if (digits >= 18) {
      return 'longer';
    }
    if (digits >= 15) {
      return 'long';
    }
    return '';
  });

  constructor() {
    // Handle click outside to close dropdown
    effect(() => {
      if (this.isDropdownOpen()) {
        // Add slight delay to prevent immediate closure when opening
        setTimeout(() => {
          const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const element = this.elementRef.nativeElement;

            // Check if click is outside the component
            if (!element.contains(target)) {
              this.closeDropdown();
              document.removeEventListener('click', handleClickOutside);
            }
          };

          document.addEventListener('click', handleClickOutside);

          // Cleanup function
          return () => {
            document.removeEventListener('click', handleClickOutside);
          };
        }, 0);
      }
    });
  }

  /**
   * Toggles the dropdown open/close state
   */
  toggleDropdown(): void {
    const willBeOpen = !this.isDropdownOpen();
    this.isDropdownOpen.set(willBeOpen);

    if (willBeOpen) {
      this.checkDropdownPosition();
    }
  }

  /**
   * Checks available space and determines whether dropdown should open above or below
   */
  private checkDropdownPosition(): void {
    const dropdownHeight = parseInt(this.maxDropdownHeight());
    const shouldOpenAbove = calculateDropdownPosition(
      this.dropdownWrapper(),
      dropdownHeight
    );
    this.dropdownOpenAbove.set(shouldOpenAbove);
  }

  /**
   * Handles currency selection from the dropdown
   */
  onCurrencySelected(currency: DropdownItem): void {
    this.currencySelected.emit(currency);
    this.isDropdownOpen.set(false);
  }

  /**
   * Closes the dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  /**
   * Gets the currency code from the selected currency
   */
  getSelectedCurrencyCode(): string {
    const selected = this.selectedCurrency();
    return selected?.id || 'USD';
  }

  /**
   * Gets the country code from the selected currency's data
   */
  getSelectedCountryCode(): string | undefined {
    const selected = this.selectedCurrency();
    return selected?.data?.countryCode;
  }

  /**
   * Gets formatted currency display text for the selector button.
   * If the label is already formatted (contains en dash), returns it as-is.
   * Otherwise, formats as: "CODE - Full Name Symbol"
   * Example: "USD – United States Dollar $"
   */
  getFormattedCurrencyDisplay(): string {
    const selected = this.selectedCurrency();
    if (!selected) {
      return '';
    }
    return selected.label;
  }

  /**
   * Transforms BalanceCurrency array to DropdownItem array.
   * Creates formatted labels and secondaryText for display.
   * Prefers CURRENCY_DATA symbols but falls back to API-provided symbols if needed.
   */
  private transformBalanceCurrencies(currencies: BalanceCurrency[]): DropdownItem[] {
    return currencies.map((currency) => {
      const currencyName = CURRENCY_DATA[currency.symbol as CurrencyCode]?.name;
      const currencySign = getCurrencySign(currency.symbol);
      const formattedBalance = this.formatNumberValue(currency.balance);

      return {
        id: currency.symbol,
        label: `<span class="dropdown__currency-code">${currency.symbol}</span><span class="dropdown__currency-dash"> – </span><span class="dropdown__currency-details">${currencyName} ${currencySign}</span>`,
        secondaryText: formattedBalance,
        secondaryTextPrefix: 'fund.currency_management.total',
        data: currency,
      };
    });
  }

  private formatNumberValue(value: number): string {
    const fmt = this.numberFormat();
    const parts = Math.abs(value).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart, fmt.digitGroupingSymbol, fmt.digitGrouping);
    const result = grouped + fmt.decimalSymbol + decPart;
    return value < 0 ? `-${result}` : result;
  }

  private applyDigitGrouping(intPart: string, groupSymbol: string, groupPattern: string): string {
    const patternParts = groupPattern.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;
    const groupSizes = patternParts.map(p => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;
    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(digits.slice(pos, pos + size).reverse().join(''));
      pos += size;
      sizeIdx++;
    }
    return groups.reverse().join(groupSymbol);
  }
}
